import CheckIcon from '@mui/icons-material/Check';
import { Box, FormControl } from '@mui/material';
import { useFormik } from 'formik';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useResetPassword } from '@/api/mutations/useAuthMutations';
import { FormHelperText } from '@/components/FormHelperText/FormHelperText';
import { LogoSvg } from '@/components/Icons/LogoSvg';
import { Loading } from '@/components/Loading/Loading';
import { Input } from '@/components/StyledComponents';
import { Button } from '@/components/ui/button';
import { Text } from '@/components/ui/text';
import { paths } from '@/constants';
import { snackbarColor } from '@/enums';
import { useSnackbar } from '@/hooks';
import { isApiError } from '@/types';
import { validateResetPassword } from '@/utils/validators';

export const ResetPasswordForm = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const showSnackbar = useSnackbar();
    const [success, setSuccsess] = useState(false);
    const validate = validateResetPassword(t);
    const url = new URL(window.location.toString());
    const token = url.searchParams.get('token');
    const email = url.searchParams.get('email');

    const { mutateAsync: resetPassword, status } = useResetPassword();

    const initialValues: { password: string; password_confirmation: string } = {
        password: '',
        password_confirmation: '',
    };

    const formik = useFormik({
        validate,
        initialValues,
        validateOnMount: true,
        isInitialValid: false,
        onSubmit: async values => {
            if (token && email) {
                try {
                    const { success } = await resetPassword({
                        token,
                        email,
                        password: values.password,
                        password_confirmation: values.password_confirmation,
                    });
                    if (success) {
                        setSuccsess(true);
                        setTimeout(() => {
                            navigate(paths.sign.index);
                        }, 3000);
                    }
                } catch (error) {
                    if (isApiError(error)) {
                        showSnackbar({
                            open: true,
                            message: error.data.message,
                            severity: snackbarColor.error,
                        });
                    }
                }
            }
        },
    });

    return (
        <Box p="0 15px" textAlign="center" position="relative">
            <div className="inline-block mb-[50px]">
                <LogoSvg />
            </div>
            {success ? (
                <div>
                    <Text variant="h3" color="text-primary" children={t('helpers.password.reset-success')} />
                </div>
            ) : (
                <form noValidate autoComplete="off" onSubmit={formik.handleSubmit}>
                    <FormControl>
                        <Input
                            id="password"
                            name="password"
                            type="password"
                            placeholder={t('placeholders.new-password')}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            value={formik.values.password}
                            endAdornment={!formik.errors.password ? <CheckIcon color="secondary" /> : null}
                        />
                        <FormHelperText error={formik.errors.password} touched={formik.touched.password} />
                    </FormControl>

                    <FormControl>
                        <Input
                            id="password_confirmation"
                            name="password_confirmation"
                            type="password"
                            placeholder={t('placeholders.repeat-password')}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            value={formik.values.password_confirmation}
                            endAdornment={!formik.errors.password_confirmation ? <CheckIcon color="secondary" /> : null}
                        />
                        <FormHelperText error={formik.errors.password_confirmation} touched={formik.touched.password_confirmation} />
                    </FormControl>
                    <Button
                        type="submit"
                        size="large"
                        className="mt-[65px] w-[320px]"
                        disabled={!formik.isValid || formik.isSubmitting}
                        variant="contained"
                    >
                        {status === 'pending' ? <Loading /> : t('buttons.save')}
                    </Button>
                </form>
            )}
        </Box>
    );
};
